import config from "config";

import { FC, Fragment, useState } from "react";
import dayjs from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useFormattedDate from "app/hooks/useFormattedDate";
import useToggle from "app/hooks/useToggle";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
import IBillingInvoice from "app/models/BillingInvoice";
// Async
import { exportInvoicesToPDF } from "app/store/BillingInvoices/BillingInvoices.async";
// Selectors
import { selectCompanyNameByAccountId } from "app/store/Clients/Clients.selectors";
// MUI
import { Box, Divider, Drawer, IconButton, Theme, Tooltip, Typography } from "@mui/material";
// Icons
import { Close as CloseIcon } from "@mui/icons-material";
// Components
import Icon from "app/components/Icon";
import { Button } from "app/components/Mui";
import { InfoBlock } from "app/components/Utilities";
//
import ACHPaymentFormDialog from "./ACHPaymentFormDialog";
import CreditCardPaymentFormDialog from "./CreditCardPaymentFormDialog";
import InvoicesListItemTransactions from "./InvoicesListItemTransactions";
import InvoicesListItemLines from "./InvoicesListItemLines";
// MixPanel
import MixpanelActions from "app/features/mixpanel/MixpanelActions";
// Utilities
import { statusThemeColor } from "app/utilities/Utilities";

type Props = {
  open: boolean;
  onClose: () => void;
  invoice: IBillingInvoice;
}

const InvoiceDetailsDrawer:FC<Props> = ({ open, onClose, invoice }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [ stateLoading, setStateLoading ] = useState<boolean>(false);

  const title = invoice.invoiceNumber ? `${t('labels.invoice')}: ${invoice.invoiceNumber}` : t('labels.invoice');

  const companyName = useAppSelector((state:RootState) => selectCompanyNameByAccountId(state, {
    accountId: invoice.insuranceCase?.clientAccountId
  }));
  
  const dueDate = useFormattedDate(invoice.dueDate);
  const periodStart = useFormattedDate(invoice.periodStart);
  const periodEnd = useFormattedDate(dayjs(invoice.periodEnd).subtract(1, 'day').toISOString());

  const dueDateIsInPast = invoice.status === 'sent' && invoice.dueDate
    ? dayjs(invoice.dueDate).isBefore(dayjs(), 'day')
    : false
  ;

  const statusLabels:Record<string, string> = {
    sent: t('labels.outstanding'),
    paid: t('labels.paid')
  }

  const handleExportInvoiceToPDF = async () => {
    setStateLoading(true)
    try {
      await dispatch(exportInvoicesToPDF([invoice.id])).unwrap();
      dispatch(MixpanelActions.handleBillingInvoicesPDFExportClick({ invoiceId: invoice.id }));
    } catch(error){} finally {
      setStateLoading(false)
    }
  }

  const { open:openACHPaymentFormDialog, toggle:toggleACHPaymentFormDialog } = useToggle();
  const { open:openCreditCardPaymentFormDialog, toggle:toggleCreditCardPaymentFormDialog } = useToggle();

  return (
    <Fragment>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: 600, p: 4 } }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2
        }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mx: -4 }} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            {invoice.description ? (
              <Typography variant="subtitle1">{invoice.description}</Typography>
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              variant="body2"
              sx={{
                textTransform: 'capitalize',
                color: (theme:Theme) => statusThemeColor(invoice.status, theme)
              }}
            >
              {statusLabels[invoice.status]}
              {dueDateIsInPast ? (
                <Box component="span" color="red">{` (${t('labels.overdue')})`}</Box>
              ) : null}
            </Typography>
          </Box>
          {periodStart && periodEnd ? (
            <InfoBlock
              label={t('labels.billingPeriod')}
              value={`${periodStart} - ${periodEnd}`}
              direction="row"
              sx={{ mt: 2 }}
            />
          ) : null}
          <InfoBlock label={t('labels.dueDate')} value={dueDate || ''} direction="row" sx={{ mt: 2 }} />
          {companyName ? (
            <Tooltip title={t('labels.clientName')}>
              <Box sx={{ maxWidth: 'max-content', display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Icon icon="team" />
                <Typography variant="subtitle2">{companyName}</Typography>
              </Box>
            </Tooltip>
          ) : null}
          {invoice.insuranceCase && invoice.insuranceCase.claimNumber  ? (
            <Tooltip title={t('labels.claimNumber')}>
              <Box sx={{ maxWidth: 'max-content', display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Icon icon="case_number" />
                <Typography variant="subtitle2">{invoice.insuranceCase.claimNumber}</Typography>
              </Box>
            </Tooltip>
          ) : null}

          <InvoicesListItemTransactions
            invoiceId={invoice.id}
            transactions={invoice.transactions}
          />
          <InvoicesListItemLines
            invoiceId={invoice.id}
            invoiceDiscountAmount={invoice.discountAmount}
            lines={invoice.lines}
          />
        </Box>
        <Divider sx={{ mx: -4 }} />
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 4,
          pt: 4
        }}>
          {invoice.status !== 'paid' ? (
            <Fragment>
              {config.domain === '.com' ? (
                <Button
                  name={`ACH payment ${invoice.id}`}
                  onClick={toggleACHPaymentFormDialog}
                  variant="contained"
                  color="primary"
                  size="small"
                >{t('pages.billingAndSubscriptions.achPaymentButton')}</Button>
              ) : null}
              <Button
                name={`Pay with credit card ${invoice.id}`}
                onClick={toggleCreditCardPaymentFormDialog}
                variant="contained"
                color="primary"
                size="small"
              >{t('pages.billingAndSubscriptions.creditCardPaymentButton')}</Button>
            </Fragment>
          ) : null}
          <Button
            name={`PDF Export ${invoice.id}`}
            onClick={handleExportInvoiceToPDF}
            variant="contained"
            color="primary"
            size="small"
            loading={stateLoading}
          >{t('pages.billingAndSubscriptions.exportToPdfButton')}</Button>
        </Box>
      </Drawer>
      {openACHPaymentFormDialog ? (
        <ACHPaymentFormDialog
          open={openACHPaymentFormDialog}
          onClose={toggleACHPaymentFormDialog}
          invoice={invoice}
        />
      ) : null}
      {openCreditCardPaymentFormDialog ? (
        <CreditCardPaymentFormDialog
          open={openCreditCardPaymentFormDialog}
          onClose={toggleCreditCardPaymentFormDialog}
          invoice={invoice}
        />
      ) : null}
    </Fragment>
  )
}

export default InvoiceDetailsDrawer;
